import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { getBase64 } from "helpers";
import {
  useUploadMissionControlFilesMutation,
  MissionControlUploadResult,
} from "src/store/api/generatedApi";

const ACCEPTED_FILE_TYPES = ".xlsx,.zip";

const UPLOAD_ICON_SIZE = 48;

const DROPZONE_TRANSITION = "all 0.2s ease";

interface MissionControlUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

export const MissionControlUploadDialog: React.FC<
  MissionControlUploadDialogProps
> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { tenantGroup } = useParams<{ tenantGroup: string }>();
  const [uploadFiles, { isLoading }] = useUploadMissionControlFilesMutation();
  const [results, setResults] = useState<MissionControlUploadResult[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);

  const handleClose = () => {
    setResults([]);
    setHasUploaded(false);
    onClose();
  };

  const handleFileUpload = useCallback(
    async (files: FileList) => {
      const filePromises = Array.from(files).map(async (file) => {
        const content = await getBase64(file);
        if (typeof content !== "string") {
          throw new Error(`Failed to read file: ${file.name}`);
        }
        return {
          name: file.name,
          content,
          updated_at: new Date(file.lastModified).toISOString(),
        };
      });

      try {
        const processedFiles = await Promise.all(filePromises);
        const response = await uploadFiles({
          missionControlUploadRequest: {
            files: processedFiles,
          },
          tenantGroupName: tenantGroup,
        }).unwrap();
        setResults(response.results);
        setHasUploaded(true);
      } catch {
        setResults([
          {
            filename: "Upload failed",
            tenant: null,
            success: false,
          },
        ]);
        setHasUploaded(true);
      }
    },
    [uploadFiles, tenantGroup]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      void handleFileUpload(e.dataTransfer.files);
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        void handleFileUpload(e.target.files);
      }
    },
    [handleFileUpload]
  );

  const successCount = results.filter((r) => r.success).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CloudUploadIcon />
        {t("uploadSavingsData")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!hasUploaded ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("scrapConsumptionUploadInfo")}
            </Alert>
            <Paper
              elevation={0}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                p: 4,
                border: "2px dashed",
                borderColor: isDragOver ? "primary.main" : "divider",
                backgroundColor: isDragOver
                  ? "action.hover"
                  : "background.default",
                textAlign: "center",
                cursor: "pointer",
                transition: DROPZONE_TRANSITION,
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                  <Typography>{t("uploading")}</Typography>
                </Box>
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: UPLOAD_ICON_SIZE,
                      color: "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t("dropFilesHere")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t("supportedFormats")}
                  </Typography>
                  <input
                    type="file"
                    multiple
                    accept={ACCEPTED_FILE_TYPES}
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                    id="mission-control-file-input"
                  />
                  <label htmlFor="mission-control-file-input">
                    <Button variant="outlined" component="span">
                      {t("selectFiles")}
                    </Button>
                  </label>
                </>
              )}
            </Paper>
          </Box>
        ) : (
          <Box>
            {successCount > 0 ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("filesUploadedSuccessfully", { count: successCount })}
                </Alert>
                <List dense>
                  {results
                    .filter((result) => result.success)
                    .map((result) => (
                      <ListItem key={result.filename}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={result.filename} />
                      </ListItem>
                    ))}
                </List>
              </>
            ) : (
              <Alert severity="warning">{t("noFilesUploaded")}</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {hasUploaded ? (
          <>
            <Button
              onClick={() => {
                setResults([]);
                setHasUploaded(false);
              }}
            >
              {t("uploadMore")}
            </Button>
            <Button variant="contained" onClick={handleClose}>
              {t("done")}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} disabled={isLoading}>
            {t("cancel")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
